import { ApolloError } from "@apollo/client";
import {
  useAllUserDocumentsByTypeQuery,
  useDeleteImmigrationDocumentMutation,
  useDeleteUserDocumentMutation,
  useExhibitFilesQuery,
  useImmigrationDocsByUserQuery,
  useUnlinkExhibitFileMutation,
} from "@codegen/index";
import {
  CompanyDocumentType,
  ImmigrationDocumentType,
  IndividualDocumentType,
} from "@codegen/schema";
import { FormContainer, OnboardingContainer } from "@components/onboarding";
import { useReadOnlyMode } from "@components/readOnlyModeProvider";
import { UploadFileStatus } from "@components/uploader/types";
import Uploader from "@components/uploader/uploader";
import {
  FileFootnote,
  ScreenshotFootnote,
} from "@pages/onboarding/shared/footnotes";
import {
  OnboardingFileFormType,
  OnboardingPageType,
} from "@pages/onboarding/types";
import { CriticalRoleInternalDocuments } from "@pages/onboarding/workflows/descriptions";
import * as Sentry from "@sentry/react";
import { api } from "@utils/api";
import { useLogError } from "@utils/error";
import { UploadStatus } from "@utils/files";
import { useJwt } from "@utils/hooks";
import * as changeCase from "change-case";
import * as React from "react";
import { useCallback, useRef, useState } from "react";
import toast from "react-hot-toast";
import { z } from "zod";
import { FormLoading } from "./loading";

export const fileFormSchema = z.object({
  files: z.string().array().min(1, "Please provide at least one file"),
});

export type FileFormType = z.infer<typeof fileFormSchema>;

const DocumentUploadForm: React.FC<
  OnboardingPageType & {
    fileType:
      | IndividualDocumentType
      | ImmigrationDocumentType
      | CompanyDocumentType;
    title: string;
    subtitle: React.ReactNode;
    description: React.ReactNode;
  } & OnboardingFileFormType
> = ({
  contact,
  onBack,
  onSubmit,
  autoSave,
  onSkip,
  isSaving,
  fileType,
  title,
  subtitle,
  description,
  footnote,
  exhibit,
  data,
  refetch,
  uploadFile,
  deleteFile,
  onSkipForNow,
  moduleProgress,
  currentStep,
  showSkipForNow,
  hideExitButton,
}) => {
  const { required } = exhibit;
  const token = useJwt();
  const logError = useLogError();
  const { isReadOnly } = useReadOnlyMode();
  const pendingSaveRef = useRef<Promise<void> | null>(null);
  const [status, setStatus] = useState<
    "idle" | "loading" | "error" | "success"
  >("idle");
  const [error, setError] = useState<string | null>(null);
  const [currentFiles, setCurrentFiles] = React.useState(
    data?.map((x) => ({
      id: x.id ?? "",
      name: x.name ?? "",
    })) ?? []
  );
  const filesRef = React.useRef(currentFiles);
  const pendingUploadsRef = React.useRef<number>(0);
  const saveQueueRef = React.useRef<Promise<void>>(Promise.resolve());

  React.useEffect(() => {
    filesRef.current = currentFiles;
  }, [currentFiles]);

  React.useEffect(() => {
    const newFiles =
      data?.map((x) => ({
        id: x.id ?? "",
        name: x.name ?? "",
      })) ?? [];
    setCurrentFiles(newFiles);
  }, [data]);

  const handleSave = useCallback(
    async (
      currentFiles: Array<{
        id: string;
        name: string;
        success?: boolean;
        fileId?: string;
      }>
    ) => {
      const savePromise = (async () => {
        try {
          setStatus("loading");
          if (currentFiles?.length && currentFiles.length > 0) {
            const files = currentFiles?.map((n) => n?.id);
            await autoSave({ files });
            await refetch();
            setStatus("success");
          } else if (!required) {
            onSkip?.();
          } else {
            setStatus("idle");
          }
        } catch (error) {
          setStatus("error");
          logError(error, { message: "Error saving files" });
        } finally {
          pendingSaveRef.current = null;
        }
      })();

      pendingSaveRef.current = savePromise;
      await savePromise;
    },
    [autoSave, logError, onSkip, refetch, required]
  );

  const handleSubmit = async () => {
    try {
      if (pendingSaveRef.current) {
        await pendingSaveRef.current;
      }
      setError(null);
      setStatus("loading");
      if (currentFiles?.length && currentFiles.length > 0) {
        await refetch();
        const files = currentFiles?.map((n) => n?.id);
        if (files?.length) {
          onSubmit({ files });
        } else {
          setError("Please upload at least one file");
          setStatus("error");
        }
      } else if (!required) {
        onSkip?.();
      } else {
        setError("Please upload at least one file");
        setStatus("error");
      }
    } catch (error) {
      setStatus("error");
      logError(error, { message: "Error submitting files" });
    }
  };

  const handleSkipForNow = async () => {
    try {
      if (currentFiles?.length && currentFiles.length > 0) {
        await refetch();
        const files = currentFiles?.map((n) => n?.id);
        onSkipForNow?.({ files });
      } else {
        onSkipForNow?.();
      }
    } catch (error) {
      logError(error, { message: "Error skipping" });
    }
  };

  const [unlinkExhibitFile] = useUnlinkExhibitFileMutation();

  const { data: exhibitFiles } = useExhibitFilesQuery({
    variables: {
      id: exhibit.exhibitId?.toString() ?? "",
    },
  });

  const handleDelete = async (file: UploadFileStatus) => {
    try {
      setError(null);
      setStatus("loading");
      const exhibitFile =
        exhibitFiles?.exhibitById?.exhibitFilesByExhibitId?.nodes?.find(
          (f) => f?.fileByFileId?.id === file.id
        );
      // Unlink from exhibit if it exists
      if (exhibit.exhibitId && exhibitFile) {
        Sentry.addBreadcrumb({
          category: "exhibit",
          message: `Unlinking exhibit file ${file.id} from exhibit ${exhibit.exhibitId}`,
        });
        await unlinkExhibitFile({
          variables: {
            input: {
              clientMutationId: `unlink-exhibit-file-${exhibit.exhibitId}-${file.id}`,
              exhibitId: exhibit.exhibitId.toString(),
              fileId: file.id,
            },
          },
        });
      }

      if (file.id) {
        await deleteFile(file.id);
      }

      await refetch();
      setCurrentFiles((prev) => prev.filter((f) => f.id !== file.id));
      setStatus("success");
    } catch (e) {
      setStatus("error");
      logError(e, { contactId: contact.id, fileId: file.id });
      toast.error("An error occurred while deleting the file");
    }
  };

  const onFileUpload = useCallback(
    async (file: File) => {
      pendingUploadsRef.current += 1;

      try {
        setStatus("loading");
        setError(null);
        const uploadResult = await uploadFile(
          file,
          token,
          contact.id,
          fileType
        );
        if (!uploadResult.success || !uploadResult.id)
          throw new Error("An error occurred while uploading the file");

        const { id } = uploadResult;
        const newFile = {
          id,
          name: file.name,
          success: uploadResult.success,
          fileId: id,
        };
        await refetch();

        // Queue the save operation to ensure sequential processing
        const savePromise = (async () => {
          // Wait for any previous save operations to complete
          await saveQueueRef.current;

          // Get the latest state after previous saves
          const currentState = filesRef.current;
          const updated = [...currentState, newFile];

          await handleSave(updated);
          setCurrentFiles(updated);
        })();

        saveQueueRef.current = savePromise;
        await savePromise;

        setStatus("success");
        return newFile;
      } catch (exception) {
        setStatus("error");
        logError(exception, {
          contactId: contact.id,
          fileType,
        });
        return {
          success: false,
          name: file.name,
          type: file.type,
          link: "",
          id: "",
        };
      } finally {
        pendingUploadsRef.current -= 1;
      }
    },
    [uploadFile, token, contact.id, fileType, refetch, handleSave, logError]
  );

  return (
    <OnboardingContainer
      key={exhibit.id}
      title={title}
      subtitle={subtitle}
      description={description}
      onSubmit={handleSubmit}
      exhibit={exhibit}
      name={`${changeCase.kebabCase(fileType)}-form`}
      footnote={footnote}
      onBack={onBack}
      onSkipForNow={required ? handleSkipForNow : onSkip}
      isLoading={isSaving || status === "loading"}
      progress={moduleProgress}
      currentStep={currentStep}
      showSkipForNow={showSkipForNow}
      hideExitButton={hideExitButton}
    >
      <FormContainer
        status={status}
        errorMessage={
          error || "There was a problem uploading your files. Please try again."
        }
        successMessage={
          status === "success" ? "Files uploaded successfully." : undefined
        }
      >
        <div>
          {data == null && (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin h-6 w-6 border-2 border-accent rounded-full border-t-transparent"></div>
            </div>
          )}
          {data != null && (
            <Uploader
              key={exhibit.id}
              multiple
              onFileUpload={onFileUpload}
              defaultFiles={currentFiles}
              disabled={isReadOnly}
              allowDelete
              onDeleteFile={handleDelete}
            />
          )}
        </div>
      </FormContainer>
    </OnboardingContainer>
  );
};

export const OneIndividualDocumentForm: React.FC<
  OnboardingPageType & {
    fileType: IndividualDocumentType;
    title: string;
    subtitle: React.ReactNode;
    description: React.ReactNode;
    exhibit: any;
  }
> = (props) => {
  const contact = props.contact;
  const { data, loading, refetch } = useAllUserDocumentsByTypeQuery({
    variables: {
      documentType: props.fileType,
      userId: props.contact.id,
    },
  });
  const logError = useLogError();
  const exhibitData = props.exhibit.data as { files?: (string | number)[] };
  const uploadFile = api.files.uploadIndividualDoc;
  const files = React.useMemo(() => {
    return (
      (data?.allUserDocuments?.nodes
        ?.map((node) => node?.fileByFileId)
        .filter(
          (n) =>
            !!n &&
            (exhibitData?.files?.includes(parseInt(n?.id)) ||
              exhibitData?.files?.includes(n?.id))
        ) as {
        id: string;
        name: string;
      }[]) ?? []
    );
  }, [data, exhibitData]);

  const [deleteUserDocument] = useDeleteUserDocumentMutation();
  const doDeleteUserDocument = React.useCallback(
    async (fileId: string) => {
      try {
        const { errors } = await deleteUserDocument({
          variables: {
            input: {
              clientMutationId: `delete-user-document-${contact.id}-${fileId}`,
              userId: contact.id,
              fileId,
            },
          },
        });
        if (errors != null) {
          throw new Error(errors[0].message);
        }
        return {
          success: true,
          error: undefined,
        };
      } catch (e) {
        if (e instanceof Error) {
          logError(e, { contactId: contact.id, fileId });
        } else if (e instanceof ApolloError) {
          logError(e.graphQLErrors[0], { contactId: contact.id, fileId });
        } else {
          logError(e, { contactId: contact.id, fileId });
        }
        return {
          success: false,
          error: UploadStatus.ErrorBackend,
        };
      }
    },
    [contact.id, deleteUserDocument, logError]
  );

  if (loading) return <FormLoading />;

  return (
    <DocumentUploadForm
      {...props}
      data={files}
      loading={loading}
      refetch={refetch}
      uploadFile={uploadFile}
      deleteFile={doDeleteUserDocument}
    />
  );
};

export const ImmigrationDocumentForm: React.FC<
  OnboardingPageType & {
    fileType: ImmigrationDocumentType;
    title: string;
    subtitle: React.ReactNode;
    description: React.ReactNode;
  }
> = (props) => {
  const contact = props.contact;
  const { data, loading, refetch } = useImmigrationDocsByUserQuery({
    variables: {
      ownerId: contact.id,
      type: props.fileType,
    },
  });
  const logError = useLogError();

  const uploadFile = api.files.uploadImmigrationDoc;

  const [deleteImmigrationDocument] = useDeleteImmigrationDocumentMutation();
  const doDeleteImmigrationDocument = React.useCallback(
    async (fileId: string) => {
      try {
        const { errors } = await deleteImmigrationDocument({
          variables: {
            input: {
              clientMutationId: `delete-user-immigration-doc-${props.fileType}-${contact.id}-${fileId}`,
              ownerId: contact.id,
              fileId,
            },
          },
        });
        if (errors != null) {
          throw new Error(errors[0].message);
        }
        return {
          success: true,
          error: undefined,
        };
      } catch (e) {
        if (e instanceof Error) {
          logError(e, { contactId: contact.id, fileId });
        } else if (e instanceof ApolloError) {
          logError(e.graphQLErrors[0], { contactId: contact.id, fileId });
        } else {
          logError(e, { contactId: contact.id, fileId });
        }
        return {
          success: false,
          error: UploadStatus.ErrorBackend,
        };
      }
    },
    [contact.id, deleteImmigrationDocument, logError, props.fileType]
  );

  if (loading) return <FormLoading />;

  return (
    <DocumentUploadForm
      {...props}
      data={
        data?.allImmigrationDocuments?.nodes
          ?.map((node) => node?.fileByFileId)
          .filter((n) => !!n) ?? []
      }
      loading={loading}
      refetch={refetch}
      uploadFile={uploadFile}
      deleteFile={doDeleteImmigrationDocument}
    />
  );
};

export const OfferLetterForm: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      fileType={IndividualDocumentType.OfferLetter}
      subtitle={name}
      title={moduleName}
      description="Please upload your original offer letter for the role, and any documents to show progressive title changes or promotions."
    />
  );
};

export const EVLetterForm: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      fileType={IndividualDocumentType.EvLetter}
      subtitle={name}
      title={moduleName}
      description="Please upload your job employment verification letter"
    />
  );
};

export const OrgChartForm: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      fileType={IndividualDocumentType.OrgChart}
      subtitle={name}
      title={moduleName}
      description={
        <p>
          Please upload an organizational chart showing your seniority or
          managerial responsibility. In order to be used in your petition, the
          org chart must clearly show the year, fit on a standard 8.5 by 11inch
          printed paper, and be signed by a senior member of your team.
        </p>
      }
    />
  );
};

export const CalendarForm: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      fileType={IndividualDocumentType.Calendar}
      subtitle={name}
      title={moduleName}
      description={<p>Please upload your calendar</p>}
    />
  );
};

export const MembershipLetterOrMailForm: React.FC<OnboardingPageType> = (
  props
) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      fileType={IndividualDocumentType.ProofOfMembershipLetterOrEmail}
      subtitle={name}
      title={moduleName}
      description="Upload proof of your exclusive professional membership, such as a member portal screenshot, acceptance email, and any other verification. Please make sure the source URLs are visible, or if an email, that all email addresses are clearly visible. Congrats—we're excited to include this in your petition!"
      footnote={ScreenshotFootnote}
    />
  );
};

export const MembershipProfilePagelForm: React.FC<OnboardingPageType> = (
  props
) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      fileType={IndividualDocumentType.ProofOfMembershipProfilePage}
      subtitle={name}
      title={moduleName}
      description="Upload proof of your exclusive professional membership, such as a member portal screenshot, acceptance email, and any other verification. Please make sure the source URLs are visible, or if an email, that all email addresses are clearly visible. Congrats—we're excited to include this in your petition!"
      footnote={ScreenshotFootnote}
    />
  );
};

export const JudgesProfileForm: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      fileType={IndividualDocumentType.Judges}
      subtitle={name}
      title={moduleName}
      description=""
    />
  );
};

export const JudgingInviteOrConfirmationForm: React.FC<OnboardingPageType> = (
  props
) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      fileType={IndividualDocumentType.JudgingInviteOrConfirmation}
      subtitle={name}
      title={moduleName}
      description={
        <p>
          Any emails or formal invitation confirming you've been selected as a
          judge in this event. This can be an email invitation, screenshot of
          your internal reviewing portal, or any other evidence showing
          communication between yourself and the organizing committee to confirm
          your role as judge.
        </p>
      }
      footnote={ScreenshotFootnote}
    />
  );
};

export const JudgingSelectionCriteriaForm: React.FC<OnboardingPageType> = (
  props
) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      fileType={IndividualDocumentType.EvidenceOfSelectionCriteria}
      subtitle={name}
      title={moduleName}
      description=""
    />
  );
};

export const JudgingScreenshotOfEventOrPrizesForm: React.FC<
  OnboardingPageType
> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      fileType={IndividualDocumentType.ScreenshotOfEventOrPrizes}
      subtitle={name}
      title={moduleName}
      description=""
      footnote={ScreenshotFootnote}
    />
  );
};

export const OCProductRoadmapForm: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      fileType={IndividualDocumentType.ProductRoadmap}
      subtitle={name}
      title={moduleName}
      description=""
    />
  );
};

export const OCTechnicalDocumentsForm: React.FC<OnboardingPageType> = (
  props
) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      fileType={IndividualDocumentType.TechnicalDoc}
      subtitle={name}
      title={moduleName}
      description=""
    />
  );
};

export const OCOriginalCodeForm: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      fileType={IndividualDocumentType.OriginalCode}
      subtitle={name}
      title={moduleName}
      description={
        <>
          <p>
            We want to show that your code contributions have been impactful.
            Screenshot your code repository, ensuring the source URL is visible
            at the top and using light mode (a light background). Include a
            screenshot of your profile page so that we can clearly attribute the
            code contributions to you. Limit to 4 screenshots.
          </p>
          <br />
          <p>
            Screenshot your code repository in light mode with the URL visible.
            If your name isn't shown, include a profile screenshot so that we
            may attribute the code contributions to you.
          </p>
        </>
      }
      footnote={ScreenshotFootnote}
    />
  );
};
export const OCUserCountOrRevenueForm: React.FC<OnboardingPageType> = (
  props
) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      fileType={IndividualDocumentType.UserCountOrRevenue}
      subtitle={name}
      title={moduleName}
      description="If your work grew the user base, increased revenue, or earned research recognition, upload dashboard evidence or relevant proof. If you are a researcher, please upload evidence that your research was cited by prominent individuals or used by companies."
      footnote={ScreenshotFootnote}
    />
  );
};

export const PitchDeckForm: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      fileType={IndividualDocumentType.PitchDeck}
      subtitle={name}
      title={moduleName}
      description="Please upload between 4-10 slides from your company pitch deck that refer to you and the critical initiative you're overseeing. Your name and title should be clearly featured on this deck."
    />
  );
};

export const VCEvidenceForm: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      fileType={IndividualDocumentType.VcEvidence}
      subtitle={name}
      title={moduleName}
      description="We are making an awards argument for your receipt of VC funding. Please include evidence of your cap table below."
    />
  );
};

export const TranscriptForm: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      fileType={IndividualDocumentType.VcEvidence}
      subtitle={name}
      title={moduleName}
      description="Please upload a certified copy of your degree, certificate, and transcripts."
    />
  );
};

export const ProofOfEnrollmentForm: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      fileType={IndividualDocumentType.VcEvidence}
      subtitle={name}
      title={moduleName}
      description="Please upload proof of current enrollment, such as a letter from your registrar or an official enrollment verification document."
    />
  );
};

export const AwardCapTableForm: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      fileType={IndividualDocumentType.CapTable}
      subtitle={name}
      title={moduleName}
      description=""
    />
  );
};

export const AwardEvidenceOfSelectivityForm: React.FC<OnboardingPageType> = (
  props
) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      fileType={IndividualDocumentType.EvidenceOfSelectivity}
      subtitle={name}
      title={moduleName}
      description=""
    />
  );
};

export const TaxReturnForm: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;

  return (
    <OneIndividualDocumentForm
      {...props}
      fileType={IndividualDocumentType.TaxReturn}
      subtitle={name}
      description="Upload your tax return showing total earnings in this role. If unavailable, provide three pay stubs."
      title={moduleName}
    />
  );
};

export const PayStubsForm: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      fileType={IndividualDocumentType.PayStubs}
      subtitle={name}
      description="Pay stubs are documents from your employer showing your salary, taxes, and deductions for each pay period. Upload three consecutive pay stubs showing your highest earnings. Include any that reflect year-end bonuses, raises, or peak compensation. Ensure they show the time period, total pay, and your location at the time."
      title={moduleName}
    />
  );
};

export const PayBonusForm: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName, company } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      fileType={IndividualDocumentType.PayBonus}
      subtitle={name}
      description={
        <p>
          Please provide any pay statements showing a large bonus you may have
          received at <em>{company?.companyName}</em>
        </p>
      }
      title={moduleName}
      footnote={FileFootnote}
    />
  );
};

export const EquityStatementForm: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName, company } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      fileType={IndividualDocumentType.EquityStatement}
      subtitle={name}
      description={
        <>
          <p>
            If you received equity as part of your role, can you provide proof
            of your vested shares at the time of your employment at{" "}
            <b>
              <em>{company?.companyName}</em>
            </b>{" "}
            and proof of their value at that time?
          </p>
          <br />
          <p>
            This could be a Carta Screenshot showing your granted equity shares
            and their valuation at{" "}
            <b>
              <em>{company?.companyName}</em>
            </b>
          </p>
          <br />
          <p>
            If share details are unavailable, upload proof of your ownership
            percentage and company valuation (e.g., a Carta screenshot of
            granted equity and valuation). For future arguments, include
            evidence of granted equity and its current value.
          </p>
        </>
      }
      title={moduleName}
      footnote={ScreenshotFootnote}
    />
  );
};

export const EvidenceOfRegconitionForm: React.FC<OnboardingPageType> = (
  props
) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      fileType={IndividualDocumentType.EvidenceOfRecognition}
      subtitle={name}
      description=""
      title={moduleName}
    />
  );
};

export const PromotionDocumentForm: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      fileType={IndividualDocumentType.PromotionEvidence}
      subtitle={name}
      description="Please provide any documentation showing promotions in this role"
      title={moduleName}
      footnote={FileFootnote}
    />
  );
};
export const PayRaiseEvidenceForm: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      fileType={IndividualDocumentType.PayRaiseEvidence}
      subtitle={name}
      description="Please provide any documentation showing pay raise in this role"
      title={moduleName}
    />
  );
};

export const CodeContributionDocumentsForm: React.FC<OnboardingPageType> = (
  props
) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      subtitle={name}
      description="Ensure the URL is visible at the top and use light mode (a light background) for the screenshot. Please ensure we have a screenshot of your profile page so that we can clearly attribute the code contributions to you. Limit to 4 screenshots."
      title={moduleName}
      fileType={IndividualDocumentType.CodeContributorPage}
      footnote={ScreenshotFootnote}
    />
  );
};

export const CodeProjectOwnershipForm: React.FC<OnboardingPageType> = (
  props
) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      subtitle={name}
      description="GitHub profile page and any pages showing your code project ownership — we want to see you're an admin / and your pulls and commits"
      title={moduleName}
      fileType={IndividualDocumentType.CodeProjectOwnership}
      footnote={FileFootnote}
    />
  );
};

export const ConferencePresentationForm: React.FC<OnboardingPageType> = (
  props
) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      subtitle={name}
      description={"Conference presentations you've given at big-name events"}
      title={moduleName}
      fileType={IndividualDocumentType.ConferencePresentation}
      footnote={FileFootnote}
    />
  );
};

export const InternalDocuments: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      subtitle={name}
      description={CriticalRoleInternalDocuments}
      title={moduleName}
      fileType={IndividualDocumentType.InternalDocs}
      footnote={ScreenshotFootnote}
    />
  );
};

export const CustomerContractDocuments: React.FC<OnboardingPageType> = (
  props
) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName, company } = module;
  const subtitle = (
    <p>
      Contracts you've signed on behalf of{" "}
      <b>{company?.companyName ?? "the company"}</b>
    </p>
  );
  return (
    <OneIndividualDocumentForm
      {...props}
      subtitle={name}
      description={subtitle}
      title={moduleName}
      fileType={IndividualDocumentType.CustomerContract}
    />
  );
};

export const AcademicContractProposalDocuments: React.FC<OnboardingPageType> = (
  props
) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      subtitle={name}
      description=""
      title={moduleName}
      fileType={IndividualDocumentType.AcademicContractProposal}
    />
  );
};

export const AcademicPapersDocuments: React.FC<OnboardingPageType> = (
  props
) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      subtitle={name}
      description=""
      title={`${moduleName}${name ? ` - ${name}` : ""}`}
      fileType={IndividualDocumentType.AcademicPaper}
    />
  );
};

export const FundingDocuments: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      subtitle={name}
      description=""
      title={moduleName}
      fileType={IndividualDocumentType.FundingDocument}
    />
  );
};

export const DistinguishedReputationDocuments: React.FC<OnboardingPageType> = (
  props
) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;
  return (
    <OneIndividualDocumentForm
      {...props}
      subtitle={name}
      description=""
      title={moduleName}
      fileType={IndividualDocumentType.DistinguishedReputation}
      footnote={FileFootnote}
    />
  );
};

export const AwardEvidenceForm: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;

  return (
    <OneIndividualDocumentForm
      {...props}
      subtitle={name}
      description=""
      title={moduleName}
      fileType={IndividualDocumentType.AwardEvidence}
    />
  );
};

export const EvidenceOfCommercialAdoptionForm: React.FC<OnboardingPageType> = (
  props
) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;

  return (
    <OneIndividualDocumentForm
      {...props}
      subtitle={name}
      description="Proof that your work has been commercially adopted or used in the real world"
      title={moduleName}
      fileType={IndividualDocumentType.EvidenceOfCommercialAdoption}
    />
  );
};

export const EvidenceOfSignificantUsageForm: React.FC<OnboardingPageType> = (
  props
) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;

  return (
    <OneIndividualDocumentForm
      {...props}
      subtitle={name}
      description={
        "Proof that a large number of people are using your technology (think in the thousands, and tens of thousands, if not more!)"
      }
      title={moduleName}
      fileType={IndividualDocumentType.EvidenceOfSignificantUsage}
      footnote={ScreenshotFootnote}
    />
  );
};

export const EvidenceIndustryAwardsForm: React.FC<OnboardingPageType> = (
  props
) => {
  const { exhibit, module } = props;
  const { name } = exhibit;
  const { name: moduleName } = module;

  return (
    <OneIndividualDocumentForm
      {...props}
      subtitle={name}
      description={
        "Please upload any proof that your novel creation has received industry awards or prizes. This could be email confirmations, letters, or your work being featured on the award website."
      }
      title={moduleName}
      fileType={IndividualDocumentType.EvidenceOfIndustryAwards}
      footnote={FileFootnote}
    />
  );
};
